import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { translate } from '@utils/i18n';
import api from '@utils/axios';
import { updateStatusesWorkOrdersPath, fetchFilteredIdsWorkOrdersPath } from 'routes';
import toast from 'react-hot-toast';

const namespace = 'features.work_orders.index.update_statuses_modal';

function UpdateStatusesModal({
  workOrderIds,
  workOrdersCount,
  disabled,
  workOrderStatusesForSelect,
  queryParams,
  onDataChanged
}) {
  const [visible, setVisible] = useState(false);
  const defaultStatus = { label: translate('status_placeholder', { namespace }), value: '' };
  const [status, setStatus] = useState(defaultStatus);
  const [workOrdersProcessed, setWorkOrdersProcessed] = useState(0);
  const shouldContinueRef = useRef(true);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const clearForm = () => {
    shouldContinueRef.current = false;
    setStatus(defaultStatus);
    setWorkOrdersProcessed(0);
    toggleDialog();
  };

  const fetchIdsUrl = useMemo(() => fetchFilteredIdsWorkOrdersPath({ new: true }), []);

  const statusOptions = useMemo(
    () =>
      workOrderStatusesForSelect.map((s) => ({ value: String(s.value), label: s.title, icon: s.icon, color: s.color })),
    [workOrderStatusesForSelect]
  );

  const statusRenderTemplate = (children, icon, color) => (
    <div className="d-flex align-items-center">
      <span className="qmb-avatar--24--status-icon">
        <i className={`fa-lg ${icon}`} style={{ color }} />
      </span>
      <span className="ml-1">{children}</span>
    </div>
  );

  const statusItemRender = (element, itemProps) => {
    const { icon, color } = itemProps.dataItem;
    if (!icon) return element;

    return React.cloneElement(element, element.props, statusRenderTemplate(element.props.children, icon, color));
  };

  const statusValueRender = (element, value) => {
    if (!value?.icon) return element;

    return React.cloneElement(
      element,
      element.props,
      statusRenderTemplate(element.props.children, value.icon, value.color)
    );
  };

  const onSubmit = () => {
    shouldContinueRef.current = true;

    const body = new FormData();
    body.append('work_order_status_id', status.value);

    if (!workOrderIds) {
      api
        .get(fetchIdsUrl, { params: queryParams })
        .then(({ data }) => {
          batchUpdate(body, data.ids_chunks.flat());
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
        });
    } else {
      workOrderIds.split(',').forEach((id) => body.append('ids[]', id));
      api
        .patch(updateStatusesWorkOrdersPath({ format: 'json' }), body, { params: queryParams })
        .then(() => {
          onDataChanged();
          toast.success(translate('success', { namespace, count: workOrdersCount }));
          clearForm();
        })
        .catch(({ response }) => {
          toast.error(response?.data?.error ?? translate('error', { namespace }));
        });
    }
  };

  const batchUpdate = (formData, ids) => {
    if (!shouldContinueRef.current) {
      console.log('Loop aborted by the user.');
      return;
    }

    if (!ids.length) {
      toast.success(translate('success', { namespace, count: workOrdersCount }));
      onDataChanged();
      clearForm();
      return;
    }

    const currentIds = ids.splice(0, 10);
    const body = new FormData();
    body.append('work_order_status_id', formData.get('work_order_status_id'));
    currentIds.forEach((id) => body.append('ids[]', id));

    api
      .patch(updateStatusesWorkOrdersPath({ format: 'json' }), body)
      .then(({ data }) => {
        setWorkOrdersProcessed((prev) => prev + data.work_order_ids.length);
        batchUpdate(formData, ids, data);
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error ?? translate('error', { namespace }));
        clearForm();
      });
  };

  return (
    <>
      <button type="button" tabIndex="0" className="qmb-control" disabled={disabled} onClick={toggleDialog}>
        <i className="fa-light fa-pen-to-square" />
        {translate('button', { namespace })}
      </button>

      <Modal isOpen={visible} onClose={clearForm} ariaLabelledBy="wo-update-status-title">
        <ModalHeader title={translate('title', { namespace })} titleId="wo-update-status-title" onClose={clearForm} />
        <ModalBody>
          <Text as="p">{translate('description', { namespace })}</Text>
          <div className="qmb-select--blank">
            <DropDownList
              name="work_order_status_id"
              textField="label"
              dataItemKey="value"
              data={statusOptions}
              itemRender={statusItemRender}
              valueRender={statusValueRender}
              value={status}
              defaultItem={defaultStatus}
              onChange={(event) => setStatus(event.target.value)}
              size="large"
            />
          </div>
        </ModalBody>
        <ModalFooter startActions={<Text>{translate('selected', { namespace, count: workOrdersCount })}</Text>}>
          {workOrdersProcessed > 0 && (
            <Text>
              {translate('processing', { namespace, count: workOrdersCount })}: {workOrdersProcessed}/{workOrdersCount}
            </Text>
          )}
          <Button onClick={clearForm}>{translate('cancel', { namespace: 'generic' })}</Button>
          <Button variant="primary" onClick={onSubmit} disabled={!status.value}>
            {translate('button', { namespace })}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

UpdateStatusesModal.propTypes = {
  workOrderIds: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool.isRequired,
  queryParams: PropTypes.object.isRequired,
  workOrdersCount: PropTypes.number.isRequired,
  workOrderStatusesForSelect: PropTypes.arrayOf(PropTypes.object),
  onDataChanged: PropTypes.func.isRequired
};

UpdateStatusesModal.defaultProps = {
  workOrderStatusesForSelect: [],
  workOrderIds: null
};

export default UpdateStatusesModal;
